import type {
  ActionGroupCandidate,
  TriggerCandidate,
} from "../../../types/candidate";
import type { GroupLocator } from "../../../stores/automation/utils/action-group-walk";
import type {
  FlowEdge,
  FlowGraph,
  FlowNode,
  FlowNodeData,
  LayoutColumn,
  LayoutItem,
} from "./types";
import { nodeId, TRIGGER_NODE_ID } from "./types";

// eslint-disable-next-line import/no-unused-modules -- consumed by the FlowCanvas component (Task 6)
export interface BuildGraphInput {
  trigger: TriggerCandidate | undefined;
  actionGroups: ActionGroupCandidate[];
  collapsedLoopIds: ReadonlySet<number>;
}

interface Ctx {
  nodes: FlowNode[];
  edges: FlowEdge[];
  collapsedLoopIds: ReadonlySet<number>;
}

const makeNode = (
  id: string,
  data: FlowNodeData,
  parentId: string | undefined,
  draggable = false,
): FlowNode => ({
  id,
  type: data.kind,
  data,
  // position is recomputed by layout on every render; (0,0) is a safe seed
  position: { x: 0, y: 0 },
  draggable,
  selectable: true,
  ...(parentId === undefined ? {} : { parentId }),
});

const makeBranchFanEdge = (source: string, target: string): FlowEdge => ({
  id: `branchFan:${source}->${target}`,
  type: "branchFan",
  source,
  target,
});

/** Reorder eligibility threaded down the recursion. `actionsDraggable` is the
 *  `draggable` flag applied to direct action nodes created in THIS stack;
 *  `isRootStack` marks the two top-level (root) stacks, which is what makes a
 *  loop appearing directly in them a *root* loop (draggable body). A loop body
 *  is draggable only when its loop is root-level; branch columns are always
 *  draggable, so a loop nested inside a branch (or inside another loop) has a
 *  non-draggable body — matching legacy `NestedLoopGroup` (no reorder). */
interface DragScope {
  actionsDraggable: boolean;
  isRootStack: boolean;
}

/** Builds the vertical stack for one container's action groups.
 *  Returns the layout items and pushes nodes into ctx. */
const buildStack = (
  groups: ActionGroupCandidate[],
  containerLocator: GroupLocator,
  parentNodeId: string | undefined,
  _prevNodeId: string | undefined,
  ctx: Ctx,
  scope: DragScope,
): LayoutItem[] => {
  const items: LayoutItem[] = [];

  groups.forEach((group) => {
    if (group.config.oneofKind === "actions") {
      group.config.actions.actions.forEach((action) => {
        const id = nodeId.action(action.hash);
        ctx.nodes.push(
          makeNode(
            id,
            {
              kind: "action",
              actionHash: action.hash,
              groupType:
                containerLocator.kind === "root"
                  ? "actions"
                  : containerLocator.kind === "loop"
                    ? "loop"
                    : "branches",
              locator: containerLocator,
            },
            parentNodeId,
            scope.actionsDraggable,
          ),
        );
        items.push({ kind: "card", nodeId: id });
      });
      return;
    }

    if (group.config.oneofKind === "loop") {
      const groupNodeId = nodeId.loopGroup(group.instanceId);
      ctx.nodes.push(
        makeNode(
          groupNodeId,
          { kind: "loopGroup", instanceId: group.instanceId },
          parentNodeId,
        ),
      );
      const isCollapsed = ctx.collapsedLoopIds.has(group.instanceId);
      if (isCollapsed) {
        items.push({ kind: "collapsedLoop", nodeId: groupNodeId });
      } else {
        const innerLocator: GroupLocator = {
          kind: "loop",
          instanceId: group.instanceId,
        };
        const innerItems = buildContainerStack(
          group.config.loop.actionGroups,
          innerLocator,
          groupNodeId,
          undefined,
          ctx,
          // a loop found in a root stack is a root loop (draggable body);
          // any loop found deeper is nested (non-draggable body)
          { actionsDraggable: scope.isRootStack, isRootStack: false },
        );
        items.push({ kind: "group", nodeId: groupNodeId, items: innerItems });
      }
      return;
    }

    if (group.config.oneofKind === "branches") {
      const blockId = nodeId.branchesBlock(group.instanceId);
      ctx.nodes.push(
        makeNode(
          blockId,
          { kind: "branchesBlock", instanceId: group.instanceId },
          parentNodeId,
        ),
      );

      const { branches } = group.config;
      const allBranches: Array<{
        hash: string;
        actionGroups: ActionGroupCandidate[];
      }> = [
        ...branches.branches.map((b) => ({
          hash: b.hash,
          actionGroups: b.actionGroups,
        })),
        ...(branches.elseBranch !== undefined
          ? [{ hash: "else", actionGroups: branches.elseBranch.actionGroups }]
          : []),
      ];

      const columns: LayoutColumn[] = allBranches.map((branch) => {
        const headerId = nodeId.branchHeader(group.instanceId, branch.hash);
        ctx.nodes.push(
          makeNode(
            headerId,
            {
              kind: "branchHeader",
              instanceId: group.instanceId,
              branchHash: branch.hash,
            },
            parentNodeId,
          ),
        );
        ctx.edges.push(makeBranchFanEdge(blockId, headerId));
        const branchLocator: GroupLocator = {
          kind: "branch",
          instanceId: group.instanceId,
          branchHash: branch.hash,
        };
        const columnItems = buildContainerStack(
          branch.actionGroups,
          branchLocator,
          parentNodeId,
          headerId,
          ctx,
          // branch columns always allow reorder, regardless of nesting
          { actionsDraggable: true, isRootStack: false },
        );
        return { headerNodeId: headerId, items: columnItems };
      });

      const isNestedBranches = containerLocator.kind !== "root";
      if (isNestedBranches) {
        // Nested Conditional Logic block: its card, then the branch fan. The
        // container's own "+ Add Action" is emitted ABOVE this block by
        // buildContainerStack (mirroring the root layout), so no add control
        // is rendered after the block (spec 1.9/1.10).
        items.push({ kind: "card", nodeId: blockId });
        items.push({ kind: "fan", columns });
      } else {
        items.push({ kind: "fan", blockNodeId: blockId, columns });
      }
    }
    // "repeat" and undefined kinds are not UI-constructible — skipped, same as legacy canvas
  });

  return items;
};

/**
 * Builds a nested container's vertical stack (a loop body or a branch column),
 * mirroring the root "Then" layout: regular action cards first, then the
 * container's own "+ Add Action" control, then any terminal Advanced Logic
 * block (Conditional Logic / Looping). The add control therefore always sits
 * ABOVE the block and stays available -- new actions land above the block,
 * and nothing renders after it (spec 1.9/1.10), matching the root block.
 */
const buildContainerStack = (
  actionGroups: ActionGroupCandidate[],
  locator: GroupLocator,
  parentNodeId: string | undefined,
  headerNodeId: string | undefined,
  ctx: Ctx,
  scope: DragScope,
): LayoutItem[] => {
  const actionsGroups = actionGroups.filter(
    (g) => g.config.oneofKind === "actions",
  );
  const advancedGroups = actionGroups.filter(
    (g) => g.config.oneofKind === "loop" || g.config.oneofKind === "branches",
  );

  const actionItems = buildStack(
    actionsGroups,
    locator,
    parentNodeId,
    headerNodeId,
    ctx,
    scope,
  );
  const addItem = pushAddActionNode(
    locator,
    parentNodeId,
    lastNodeIdOf(actionItems) ?? headerNodeId,
    ctx,
  );
  const advancedItems = buildStack(
    advancedGroups,
    locator,
    parentNodeId,
    lastNodeIdOf(actionItems) ?? headerNodeId,
    ctx,
    scope,
  );

  return [...actionItems, addItem, ...advancedItems];
};

const lastNodeIdOf = (items: LayoutItem[]): string | undefined => {
  const last = items[items.length - 1];
  if (last === undefined) return undefined;
  if (last.kind === "card") return last.nodeId;
  if (last.kind === "group") return last.nodeId;
  return undefined; // fan — nothing chains after it
};

const pushAddActionNode = (
  locator: GroupLocator,
  parentNodeId: string | undefined,
  _prevNodeId: string | undefined,
  ctx: Ctx,
): LayoutItem => {
  const id = nodeId.addAction(locator);
  ctx.nodes.push(makeNode(id, { kind: "addAction", locator }, parentNodeId));
  return { kind: "card", nodeId: id };
};

export const buildGraph = (input: BuildGraphInput): FlowGraph => {
  const ctx: Ctx = {
    nodes: [],
    edges: [],
    collapsedLoopIds: input.collapsedLoopIds,
  };
  const layout: LayoutItem[] = [];

  if (input.trigger === undefined && input.actionGroups.length === 0) {
    return { nodes: [], edges: [], layout: [] };
  }

  if (input.trigger !== undefined) {
    // legacy Sidebar "When / this happens" header always sits over the trigger
    ctx.nodes.push(
      makeNode(
        TRIGGER_NODE_ID,
        { kind: "trigger", sectionLabel: "when" },
        undefined,
      ),
    );
    layout.push({ kind: "card", nodeId: TRIGGER_NODE_ID });
  }

  // legacy Sidebar/index.tsx render order at the root: trigger → root actions
  // list → AddActionButton (always rendered) → root Looping → root Branches
  const rootActionsGroups = input.actionGroups.filter(
    (g) => g.config.oneofKind === "actions",
  );
  const rootLoopGroups = input.actionGroups.filter(
    (g) => g.config.oneofKind === "loop",
  );
  const rootBranchesGroups = input.actionGroups.filter(
    (g) => g.config.oneofKind === "branches",
  );
  const rootLocator: GroupLocator = {
    kind: "root",
    instanceId: rootActionsGroups[0]?.instanceId ?? 0,
  };

  // "Then / do this" section header. When the section leads with a draggable
  // action card, the header is emitted as its OWN non-draggable node ABOVE it —
  // never baked into the first action — so dragging the first card no longer
  // drags the header and every action card keeps the same measured geometry
  // (the drop-slot math compares uniform card boxes). Pushed before the action
  // stack so it leads the section in both node order and layout order.
  const hasRootActions = rootActionsGroups.some(
    (group) =>
      group.config.oneofKind === "actions" &&
      group.config.actions.actions.length > 0,
  );
  if (hasRootActions) {
    const thenLabelId = nodeId.sectionLabel("then", rootLocator);
    ctx.nodes.push(
      makeNode(thenLabelId, { kind: "sectionLabel", label: "then" }, undefined),
    );
    layout.push({ kind: "card", nodeId: thenLabelId });
  }

  const rootActionItems = buildStack(
    rootActionsGroups,
    rootLocator,
    undefined,
    // Legacy When/Then are separate sections — no step edge crosses from the
    // trigger card into the Then action stack (each card carries its own line).
    undefined,
    ctx,
    { actionsDraggable: true, isRootStack: true },
  );
  layout.push(...rootActionItems);

  // Legacy Sidebar never draws a connector from the trigger card down to the
  // root add-action button — only between consecutive action cards. Chain the
  // add site from the last root action when one exists, otherwise leave it
  // unconnected (empty create flow: trigger + add-action, no spine).
  layout.push(
    pushAddActionNode(
      rootLocator,
      undefined,
      lastNodeIdOf(rootActionItems),
      ctx,
    ),
  );

  const rootAdvancedItems = buildStack(
    [...rootLoopGroups, ...rootBranchesGroups],
    rootLocator,
    undefined,
    nodeId.addAction(rootLocator),
    ctx,
    { actionsDraggable: true, isRootStack: true },
  );
  layout.push(...rootAdvancedItems);

  // Empty root action list: there is no draggable action to protect, so the
  // always-present (non-draggable) add-action button carries the "Then" header,
  // exactly as the legacy layout did.
  if (!hasRootActions) {
    const addNode = ctx.nodes.find(
      (node) => node.id === nodeId.addAction(rootLocator),
    );
    if (addNode !== undefined) {
      addNode.data.sectionLabel = "then";
    }
  }

  return { nodes: ctx.nodes, edges: ctx.edges, layout };
};
